<template>
  <div class="candidate-status-container">
    <div class="current-status">{{ currentStatus.i18nName }}</div>
    <el-progress class="progress" color="#0096cc" define-back-color="#c6c8cc" :show-text="false" :stroke-width="16" :percentage="currentStatusPercentage"></el-progress>
    <div class="status-list">
      <span
        v-for="(group, index) in statusGroupList"
        class="status-item"
        :key="index"
        :class="group === currentGroup && 'active'"
      >{{ group.i18nName }}</span>
    </div>
    <div class="foot-btn-container">
      <el-button v-if="tooltip" v-auto-tooltip.always="tooltip" class="foot-btn reject-btn" :disabled="disableChangeButton" @click="handleRejectBtnClick">{{ $t(`${i18nPrefix}.btn.reject`) }}</el-button>
      <el-button v-else class="foot-btn reject-btn" :disabled="disableChangeButton" @click="handleRejectBtnClick">{{ $t(`${i18nPrefix}.btn.reject`) }}</el-button>
      <el-dropdown
        ref="statusDropdown"
        class="move-forward-container"
        placement="bottom"
        trigger="click"
        :disabled="disableChangeButton"
      >
        <el-button v-if="tooltip" v-auto-tooltip.always="tooltip" class="foot-btn move-forward-btn" :disabled="disableChangeButton">{{ $t(`${i18nPrefix}.btn.moveForward`) }}</el-button>
        <el-button v-else class="foot-btn move-forward-btn" :disabled="disableChangeButton">{{ $t(`${i18nPrefix}.btn.moveForward`) }}</el-button>
        <template #dropdown>
          <el-dropdown-menu class="status-dropdown">
            <el-input
              v-model="search.keyWords"
              class="status-searcher"
              prefix-icon="el-icon-search"
            >
            </el-input>
            <div class="status-list-container">
              <template v-if="searchedStatusGroupList.length > 0">
                <div v-for="(group, index) in searchedStatusGroupList" class="list-item" :key="index">
                  <div class="list-group">{{ group.i18nName }}</div>
                  <ul class="list-children">
                    <li
                      v-for="(status, statusIndex) in group._searchedStatusList"
                      class="children-item"
                      :key="statusIndex"
                      :class="[status.key === currentStatus.key && 'is-active', status.has_referral ? 'has-referral' : '']"
                      @click="changeStatus(status)"
                    >
                      <span class="status-name">{{ status.i18nName }}</span>
                      <i v-if="status.has_referral" class="iconfont iconhongbao1"></i>
                    </li>
                  </ul>
                </div>
              </template>
              <template v-else>
                <div class="list-item no-matched-data">{{ $t('common.message.noMatchedData') }}</div>
              </template>
            </div>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { ROOT_COMPANY_ID } from '@/constant/company'
import { mapGetters } from 'vuex'

export default {
  props: {
    currentStatus: {
      type: Object,
      required: true
    },
    statusGroupList: {
      type: Array,
      required: true
    },
    disableChangeButton: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      search: {
        keyWords: null
      },
      loading: false,
      loaded: false
    }
  },
  computed: {
    isMSDCompany () {
      const currentAccount = this.getCurrentAccount()
      const rootCompanyId = currentAccount.root_company_id
      return rootCompanyId === ROOT_COMPANY_ID.MSD
    },
    searchedStatusGroupList () {
      const keyWords = (this.search.keyWords || '').toLowerCase()
      return _.filter(this.statusGroupList, group => {
        group._searchedStatusList = _.filter(group.status_list, status => {
          if (this.isMSDCompany && ['Hired', 'Disqualified'].includes(status.key)) {
            return false
          }
          if (!status.canChangeTo) {
            return false
          }
          if (keyWords) {
            return status.i18nName.toLowerCase().includes(keyWords)
          }
          return true
        })
        return group._searchedStatusList.length > 0
      })
    },
    statusKeyGroupIndexMap () {
      const statusKeyGroupIndexMap = {}
      _.forEach(this.statusGroupList, (group, index) => {
        _.forEach(group.status_list, status => {
          statusKeyGroupIndexMap[status.key] = index
        })
      })
      return statusKeyGroupIndexMap
    },
    currentGroup () {
      const groupIndex = this.statusKeyGroupIndexMap[this.currentStatus.key] || 0
      return this.statusGroupList[groupIndex]
    },
    currentStatusPercentage () {
      const groupLength = this.statusGroupList.length
      const currentStatusGroupIndex = _.findIndex(this.statusGroupList, group => group === this.currentGroup)
      let percentage = 0
      if (currentStatusGroupIndex > -1) {
        percentage = 100 / (groupLength - 1) * currentStatusGroupIndex
      }
      return percentage || 5
    }
  },
  created () {

  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    handleRejectBtnClick () {
      this.$emit('toRejectStatus')
    },
    changeStatus (status) {
      if (this.currentStatus.key === status.key) {
        return
      }
      this.search.keyWords = null
      this.$emit('changeStatus', status)
    }
  },
  watch: {
    currentStatus: {
      immediate: true,
      handler () {
        if (!this.currentStatus.i18nName) {
          addI18nProperty(this.currentStatus, 'i18nName', 'name_cn', 'name_en')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-status-container {
  .current-status {
    color: #0096cc;
    font-size: 16px;
    line-height: 22px;
  }

  .progress {
    margin-top: 12px;

    ::v-deep .el-progress-bar__outer {
      background: #c6c8cc;
    }
  }

  .status-list {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    color: #909399;
    line-height: 20px;

    .status-item {
      flex-shrink: 0;
      margin-left: 38px;

      &:first-child {
        margin-left: 0;
      }

      &.active {
        color: #606266;
      }
    }
  }

  .foot-btn-container {
    display: flex;
    margin-top: 30px;

    .foot-btn {
      padding: 4px 55px;
      color: #0096cc;
      border: 1px solid #0096cc;
      line-height: 20px;

      &:not(.is-disabled):hover {
        color: #fff;
        background-color: #0096cc;
      }

      &.move-forward-btn {
        margin-left: 14px;
        color: #ffffff;
        background-color: #0096cc;
      }

      &.is-disabled {
        color: #c0c4cc;
        border-color: #ebeef5;
        background-color: #ffffff;

        &.move-forward-btn {
          color: #ffffff;
          background-color: #c0c4cc;
          border-color: #c0c4cc;
        }
      }
    }

    .move-forward-container {

    }
  }
}

.status-dropdown {
  padding-top: 0;

  ::v-deep .status-searcher {
    padding: 5px 0;
    border-bottom: 1px solid #D8DCE6;

    .el-input__inner {
      height: 30px;
      line-height: 30px;
      border: transparent;
    }
  }

  .status-list-container {
    max-width: 209px;
    max-height: 350px;
    font-size: 14px;
    color: #606266;
    overflow: hidden auto;

    .list-item {
      .list-group {
        padding: 6px 0 6px 16px;
      }

      .list-children {
        .children-item {
          padding: 6px 0 6px 30px;
          cursor: pointer;

          &:hover {
            background-color: #E0E0E0;
          }

          &.is-active {
            color: #fff;
            background-color: #0096cc;
          }

          &.has-referral {
          }

          .status-name {
            vertical-align: middle;
          }

          .iconfont {
            font-size: 24px;
            vertical-align: middle;
            &::before {
              display: inline-block;
              margin-bottom: 3px;
            }
          }
        }
      }

      &.no-matched-data {
        margin-top: 10px;
        text-align: center;
      }
    }
  }
}
</style>
